import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, TextField, Button,
  InputAdornment, Card, CardContent, Typography, TableContainer, Table,
  TableHead, TableRow, TableCell, TableBody, Paper, MenuItem, TablePagination,
  useTheme, Theme, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { EquipmentDefinition, EquipmentType, Meta } from '../types';
import { equipmentApi } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import { useSnackbar } from 'notistack';
import { useConfirmDialog } from '../hooks/UseConfirmDialog';
import { gradientButtonSx } from './SharedPageParts';
import { unwrapListResponse, unwrapMetaResponse } from '../apiUnwrapper/apiUnwrapper';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: () => void;
}

// equipmentType is the full object — never just an ID string — because the
// API sends/receives the nested object, not a bare foreign key.
interface ModelFormState {
  equipmentModel: string;
  manufacturer: string;
  powerSource: string;
  capacityM3: string;
  fuelCapacity: string;
  payloadT: string;
  equipmentType: EquipmentType | null;
}

// Flat string fields that onFormChange handles generically.
// equipmentType is intentionally excluded — it is updated via onTypeChange.
type FlatFormField = Exclude<keyof ModelFormState, 'equipmentType'>;

const EMPTY_FORM: ModelFormState = {
  equipmentModel: '',
  manufacturer: '',
  powerSource: '',
  capacityM3: '',
  fuelCapacity: '',
  payloadT: '',
  equipmentType: null,
};

type EditTarget = (EquipmentDefinition & { _formState: ModelFormState }) | null;

const DEFAULT_META: Meta = { total: 0, size: 5, page: 1 };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const defToForm = (def: EquipmentDefinition, allTypes: EquipmentType[]): ModelFormState => {
  // The API may return equipmentType: null even when an equipmentTypeId exists.
  // Resolve the full object from the locally-loaded list as a fallback.
  const resolvedType =
    def.equipmentType ??
    allTypes.find((t) => t.id === (def as any).equipmentTypeId) ??
    null;

  return {
    equipmentModel: def.equipmentModel ?? '',
    manufacturer: def.manufacturer ?? '',
    powerSource: def.powerSource ?? '',
    capacityM3: def.capacityM3 != null ? String(def.capacityM3) : '',
    fuelCapacity: def.fuelCapacity != null ? String(def.fuelCapacity) : '',
    payloadT: def.payloadT != null ? String(def.payloadT) : '',
    equipmentType: resolvedType,
  };
};

const parseNum = (val: string): number | undefined =>
  val.trim() === '' ? undefined : Number(val);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ManageEquipmentModelsPopUp: React.FC<Props> = ({ open, onClose }) => {
  const theme: Theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { confirmState, confirm, closeConfirm, executeConfirm } = useConfirmDialog();

  // ── Data state ────────────────────────────────────────────────────────────
  const [modelList, setModelList]           = useState<EquipmentDefinition[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
  const [meta, setMeta]                     = useState<Meta>(DEFAULT_META);
  const [loading, setLoading]               = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]   = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm]         = useState<ModelFormState>(EMPTY_FORM);
  const [editTarget, setEditTarget]   = useState<EditTarget>(null);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadModels = useCallback(async (page = 1, size = DEFAULT_META.size) => {
    try {
      setLoading(true);
      const res = await equipmentApi.getEquipmentDefinitions(page, size);
      setModelList(unwrapListResponse<EquipmentDefinition>(res) ?? []);
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      enqueueSnackbar(`Failed to load equipment models: ${err}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const searchModels = useCallback(async (term: string, page = 1, size = DEFAULT_META.size) => {
    try {
      setLoading(true);
      const res = await equipmentApi.getEquipmentDefinitionsForSearchTerm(term, page, size);
      setModelList(unwrapListResponse<EquipmentDefinition>(res) ?? []);
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      enqueueSnackbar(`Failed to search equipment models: ${err}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadEquipmentTypes = useCallback(async () => {
    try {
      const res = await equipmentApi.getEquipmentTypes();
      setEquipmentTypes(unwrapListResponse<EquipmentType>(res) ?? []);
    } catch (err) {
      enqueueSnackbar(`Failed to load equipment types: ${err}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Load everything when dialog opens — models + types run in parallel
  useEffect(() => {
    if (open) {
      Promise.all([loadModels(1, DEFAULT_META.size), loadEquipmentTypes()]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pagination & search ───────────────────────────────────────────────────

  const handleSearch = useCallback(() => {
    if (searchTerm.trim()) searchModels(searchTerm, 1, meta.size);
    else loadModels(1, meta.size);
  }, [searchTerm, meta.size, searchModels, loadModels]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    if (searchTerm.trim()) searchModels(searchTerm, newPage + 1, meta.size);
    else loadModels(newPage + 1, meta.size);
  }, [searchTerm, meta.size, searchModels, loadModels]);

  const handleRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value, 10);
    if (searchTerm.trim()) searchModels(searchTerm, 1, size);
    else loadModels(1, size);
  }, [searchTerm, searchModels, loadModels]);

  // ── After any mutation ────────────────────────────────────────────────────

  const afterMutation = useCallback(async () => {
    if (searchTerm.trim()) await searchModels(searchTerm, meta.page, meta.size);
    else await loadModels(meta.page, meta.size);
  }, [searchTerm, meta.page, meta.size, searchModels, loadModels]);

  // ── Add ───────────────────────────────────────────────────────────────────

  const handleAdd = useCallback(async () => {
    if (!addForm.equipmentModel.trim() || !addForm.equipmentType?.id) return;
    try {
      await equipmentApi.addEquipmentDefinition({
        equipmentModel: addForm.equipmentModel,
        manufacturer: addForm.manufacturer || undefined,
        powerSource: addForm.powerSource || undefined,
        capacityM3: parseNum(addForm.capacityM3),
        fuelCapacity: parseNum(addForm.fuelCapacity),
        payloadT: parseNum(addForm.payloadT),
        equipmentType: addForm.equipmentType,
      });
      enqueueSnackbar('Equipment model created successfully', { variant: 'success' });
      setAddForm(EMPTY_FORM);
      setShowAddForm(false);
      await afterMutation();
    } catch (err) {
      enqueueSnackbar(`Error creating equipment model: ${err}`, { variant: 'error' });
    }
  }, [addForm, afterMutation, enqueueSnackbar]);

  // ── Update ────────────────────────────────────────────────────────────────

  const handleUpdate = useCallback(async () => {
    if (!editTarget) return;
    const f = editTarget._formState;
    try {
      await equipmentApi.updateEquipmentDefinition({
        id: editTarget.id,
        equipmentModel: f.equipmentModel,
        manufacturer: f.manufacturer || undefined,
        powerSource: f.powerSource || undefined,
        capacityM3: parseNum(f.capacityM3),
        fuelCapacity: parseNum(f.fuelCapacity),
        payloadT: parseNum(f.payloadT),
        equipmentType: f.equipmentType,
      });
      enqueueSnackbar('Equipment model updated successfully', { variant: 'success' });
      setEditTarget(null);
      await afterMutation();
    } catch (err) {
      enqueueSnackbar(`Error updating equipment model: ${err}`, { variant: 'error' });
    }
  }, [editTarget, afterMutation, enqueueSnackbar]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteRequest = useCallback((id: number) => {
    confirm('Are you sure you want to delete this equipment model?', async () => {
      try {
        await equipmentApi.deleteEquipmentDefinition(id);
        enqueueSnackbar('Equipment model deleted successfully', { variant: 'success' });
        await afterMutation();
      } catch (err) {
        enqueueSnackbar(`Error deleting equipment model: ${err}`, { variant: 'error' });
      }
    });
  }, [confirm, enqueueSnackbar, afterMutation]);

  // ── Reset on close ────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    setShowAddForm(false);
    setAddForm(EMPTY_FORM);
    setEditTarget(null);
    setSearchTerm('');
    onClose();
  }, [onClose]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Manage Equipment Models
          <IconButton aria-label="close" onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 4, pt: 2, height: 640, overflowY: 'auto' }}>

          {/* Search + Add row */}
          <Box display="flex" gap={2} mb={2} mt={1}>
            <TextField
              placeholder="Search by model name or manufacturer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} edge="end">
                      <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => setShowAddForm(true)}
              disabled={showAddForm}
              sx={gradientButtonSx}>
              Add New
            </Button>
          </Box>

          {/* Collapsible add form */}
          {showAddForm && (
            <Card variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                New Equipment Model
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <TextField
                  label="Model Name *"
                  value={addForm.equipmentModel}
                  onChange={(e) => setAddForm((p) => ({ ...p, equipmentModel: e.target.value }))}
                />
                <TextField
                  label="Manufacturer"
                  value={addForm.manufacturer}
                  onChange={(e) => setAddForm((p) => ({ ...p, manufacturer: e.target.value }))}
                />
                <TextField
                  select
                  label="Equipment Type *"
                  value={addForm.equipmentType?.id != null ? String(addForm.equipmentType.id) : ''}
                  onChange={(e) => {
                    const selected = equipmentTypes.find((t) => t.id === Number(e.target.value)) ?? null;
                    setAddForm((p) => ({ ...p, equipmentType: selected }));
                  }}
                >
                  {equipmentTypes.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>{t.equipmentName}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Capacity (m³)"
                  type="number"
                  inputProps={{ min: 0, step: 0.1 }}
                  value={addForm.capacityM3}
                  onChange={(e) => setAddForm((p) => ({ ...p, capacityM3: e.target.value }))}
                />
                <TextField
                  label="Fuel Tank Capacity (l)"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={addForm.fuelCapacity}
                  onChange={(e) => setAddForm((p) => ({ ...p, fuelCapacity: e.target.value }))}
                />
                <TextField
                  label="Payload Capacity (t)"
                  type="number"
                  inputProps={{ min: 0, step: 0.1 }}
                  value={addForm.payloadT}
                  onChange={(e) => setAddForm((p) => ({ ...p, payloadT: e.target.value }))}
                />
                <TextField
                  label="Power Source"
                  value={addForm.powerSource}
                  onChange={(e) => setAddForm((p) => ({ ...p, powerSource: e.target.value }))}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                <Button variant="contained" sx={gradientButtonSx} onClick={handleAdd}
                  disabled={!addForm.equipmentModel.trim() || !addForm.equipmentType?.id}>
                  Save
                </Button>
                <Button variant="outlined"
                  onClick={() => { setShowAddForm(false); setAddForm(EMPTY_FORM); }}>
                  Cancel
                </Button>
              </Box>
            </Card>
          )}

          {/* Table card */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Equipment Models ({meta.total})</Typography>
                <TablePagination
                  component="div"
                  count={meta.total}
                  page={meta.page - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={meta.size}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 15, 20]}
                  labelRowsPerPage="Per page"
                />
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#667eea' }} />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
                        {['Model Name', 'Manufacturer', 'Type', 'Capacity (m³)', 'Fuel Cap. (l)', 'Payload (t)', 'Power Source', 'Actions'].map((h) => (
                          <TableCell key={h} align={h === 'Actions' ? 'right' : 'left'}
                            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {modelList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                            <LocalShippingIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
                            <Typography variant="h6" color="text.secondary">No equipment models found</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm ? 'Try adjusting your search' : 'No models have been added yet'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        modelList.map((model) => (
                          <EquipmentModelRow
                            key={model.id}
                            model={model}
                            isEditing={editTarget?.id === model.id}
                            formState={editTarget?.id === model.id ? editTarget._formState : null}
                            equipmentTypes={equipmentTypes}
                            onFormChange={(field, value) =>
                              setEditTarget((prev) =>
                                prev ? { ...prev, _formState: { ...prev._formState, [field]: value } } : prev
                              )
                            }
                            onTypeChange={(type) =>
                              setEditTarget((prev) =>
                                prev ? { ...prev, _formState: { ...prev._formState, equipmentType: type } } : prev
                              )
                            }
                            onStartEdit={() => setEditTarget({ ...model, _formState: defToForm(model, equipmentTypes) })}
                            onSaveEdit={handleUpdate}
                            onCancelEdit={() => setEditTarget(null)}
                            onDelete={() => handleDeleteRequest(model.id)}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination
                component="div"
                count={meta.total}
                page={meta.page - 1}
                onPageChange={handlePageChange}
                rowsPerPage={meta.size}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 15, 20]}
                labelRowsPerPage="Per page"
              />
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmState.open}
        onClose={closeConfirm}
        onConfirm={executeConfirm}
        title="Confirm Delete"
        message={confirmState.message}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// EquipmentModelRow — memoized inline-edit row
// ---------------------------------------------------------------------------

interface RowProps {
  model: EquipmentDefinition;
  isEditing: boolean;
  formState: ModelFormState | null;
  equipmentTypes: EquipmentType[];
  // Handles flat string fields (equipmentModel, manufacturer, numeric strings)
  onFormChange: (field: FlatFormField, value: string) => void;
  // Handles the nested equipmentType object separately
  onTypeChange: (type: EquipmentType | null) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
}

const EquipmentModelRow: React.FC<RowProps> = React.memo(({
  model, isEditing, formState, equipmentTypes,
  onFormChange, onTypeChange, onStartEdit, onSaveEdit, onCancelEdit, onDelete,
}) => {
  // Prefer the nested object if the API returned it; otherwise look it up
  // from the locally-loaded equipmentTypes list by equipmentTypeId.
  const typeName =
    model.equipmentType?.equipmentName ??
    equipmentTypes.find((t) => t.id === (model as any).equipmentTypeId)?.equipmentName ??
    '—';

  return (
    <TableRow hover>
      <TableCell>
        {isEditing
          ? <TextField size="small" value={formState?.equipmentModel ?? ''}
              onChange={(e) => onFormChange('equipmentModel', e.target.value)} />
          : model.equipmentModel}
      </TableCell>

      <TableCell>
        {isEditing
          ? <TextField size="small" value={formState?.manufacturer ?? ''}
              onChange={(e) => onFormChange('manufacturer', e.target.value)} />
          : (model.manufacturer ?? '—')}
      </TableCell>

      <TableCell>
        {isEditing
          ? (
            <TextField
              select
              size="small"
              value={formState?.equipmentType?.id != null ? String(formState.equipmentType.id) : ''}
              onChange={(e) => {
                const selected = equipmentTypes.find((t) => t.id === Number(e.target.value)) ?? null;
                onTypeChange(selected);
              }}
              sx={{ minWidth: 160 }}
            >
              {equipmentTypes.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>{t.equipmentName}</MenuItem>
              ))}
            </TextField>
          )
          : typeName}
      </TableCell>

      <TableCell>
        {isEditing
          ? <TextField size="small" type="number" inputProps={{ min: 0, step: 0.1 }}
              value={formState?.capacityM3 ?? ''}
              onChange={(e) => onFormChange('capacityM3', e.target.value)}
              sx={{ width: 90 }} />
          : (model.capacityM3 != null ? `${model.capacityM3} m³` : '—')}
      </TableCell>

      <TableCell>
        {isEditing
          ? <TextField size="small" type="number" inputProps={{ min: 0, step: 1 }}
              value={formState?.fuelCapacity ?? ''}
              onChange={(e) => onFormChange('fuelCapacity', e.target.value)}
              sx={{ width: 90 }} />
          : (model.fuelCapacity != null ? `${model.fuelCapacity} l` : '—')}
      </TableCell>

      <TableCell>
        {isEditing
          ? <TextField size="small" type="number" inputProps={{ min: 0, step: 0.1 }}
              value={formState?.payloadT ?? ''}
              onChange={(e) => onFormChange('payloadT', e.target.value)}
              sx={{ width: 90 }} />
          : (model.payloadT != null ? `${model.payloadT} t` : '—')}
      </TableCell>

      <TableCell>
        {isEditing
          ? <TextField size="small" type="string" inputProps={{ min: 0, step: 0.1 }}
              value={formState?.powerSource ?? ''}
              onChange={(e) => onFormChange('powerSource', e.target.value)}
              sx={{ width: 90 }} />
          : (model.powerSource != null ? `${model.powerSource}` : '—')}
      </TableCell>

      <TableCell align="right">
        {isEditing ? (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" variant="contained" sx={gradientButtonSx} onClick={onSaveEdit}
              disabled={!formState?.equipmentModel.trim() || !formState?.equipmentType?.id}>
              Save
            </Button>
            <Button size="small" variant="outlined" onClick={onCancelEdit}>Cancel</Button>
          </Box>
        ) : (
          <>
            <IconButton size="small" onClick={onStartEdit} aria-label={`Edit ${model.equipmentModel}`}>
              <EditIcon sx={{ color: '#667eea' }} />
            </IconButton>
            <IconButton size="small" onClick={onDelete} aria-label={`Delete ${model.equipmentModel}`}>
              <DeleteIcon sx={{ color: '#ef4444' }} />
            </IconButton>
          </>
        )}
      </TableCell>
    </TableRow>
  );
});

EquipmentModelRow.displayName = 'EquipmentModelRow';

export default ManageEquipmentModelsPopUp;